import { memo } from "react";
import PropTypes from "prop-types";
import { Box, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { contactMailto } from "../content/legal-status.js";

// Height in px of the bar. index.js and Protected.js subtract it from the space they give the page.
export const LEGAL_BAR_HEIGHT = 30;

const linkSx = {
	// 12px text, #1f2d45 on white is 14:1
	color: "#1f2d45",
	fontSize: "12px",
	fontFamily: "inherit",
	lineHeight: `${LEGAL_BAR_HEIGHT - 2}px`,
	whiteSpace: "nowrap",
	textDecoration: "underline",
	"&:hover": { textDecorationThickness: "2px" },
	"&:focus-visible": { outline: "2px solid #1f2d45", outlineOffset: "1px", borderRadius: "2px" },
};

// Full label from sm up; a short one below (xs), so the three links fit in 375 px in every UI language.
// display:none removes the hidden variant from the accessibility tree.
const Label = ({ full, short }) => (
	<>
		<Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{full}</Box>
		<Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>{short}</Box>
	</>
);

Label.propTypes = { full: PropTypes.string.isRequired, short: PropTypes.string.isRequired };

/**
 * Permanent bar with the links every user must be able to reach: data use & privacy, terms, and contact.
 *
 * The contact entry has three states:
 * - `onContact` given: a button that calls it (the signed-in contact dialog).
 * - no `onContact`, FEEDBACK_EMAIL set: a mailto link.
 * - neither: the entry is left out.
 */
const LegalLinks = ({ onContact }) => {
	const { t } = useTranslation();
	const mailto = contactMailto(process.env.FEEDBACK_EMAIL);

	let contact = null;
	if (typeof onContact === "function") {
		contact = <Link component="button" sx={{ ...linkSx, border: 0, background: "none", cursor: "pointer", p: 0 }} type="button" onClick={onContact}><Label full={t("legal.legalPages.contactLink")} short={t("legal.legalPages.contactLinkShort")} /></Link>;
	} else if (mailto) {
		contact = <Link href={mailto} sx={linkSx}><Label full={t("legal.legalPages.contactLink")} short={t("legal.legalPages.contactLinkShort")} /></Link>;
	}

	return (
		<Box
			aria-label={t("legal.legalPages.navLabel")}
			component="nav"
			sx={{
				// Above <main>, whose fixed children (the sidebar) would otherwise paint over the bar
				position: "relative",
				zIndex: 1,
				boxSizing: "border-box",
				height: `${LEGAL_BAR_HEIGHT}px`,
				flexShrink: 0,
				display: "flex",
				alignItems: "center",
				// Not justifyContent:center: centred overflow clips the start edge so it cannot be scrolled to.
				// Auto margins on the first and last child centre the links when they fit and start-align them when they do not.
				justifyContent: "flex-start",
				"& > :first-child": { ml: "auto" },
				"& > :last-child": { mr: "auto" },
				columnGap: { xs: 1.5, sm: 2 },
				px: 1,
				// Fallback if the links still overflow (very narrow screens, large text): they scroll, and a focused link scrolls into view.
				// The scrollbar is hidden because a classic one (index.scss) would take ~13 px of the 30 px bar.
				overflowX: "auto",
				overflowY: "hidden",
				scrollbarWidth: "none",
				"&::-webkit-scrollbar": { display: "none" },
				backgroundColor: "white",
				borderTop: "1px solid rgba(0, 0, 0, 0.12)",
			}}
		>
			<Link component={RouterLink} sx={linkSx} to="/privacy"><Label full={t("legal.legalPages.privacyLink")} short={t("legal.legalPages.privacyLinkShort")} /></Link>
			<Link component={RouterLink} sx={linkSx} to="/terms">{t("legal.legalPages.termsLink")}</Link>
			{contact}
		</Box>
	);
};

LegalLinks.propTypes = { onContact: PropTypes.func };

export default memo(LegalLinks);
